/** Spec 031 admin publish rules. Pure, so the dialog can be tested without Apollo. */

const DATE = /^\d{4}-\d{2}-\d{2}$/;

export type PublishGate = {
  draftId: string | null;
  changeSummary: string;
  effectiveDate: string;
  acknowledgmentVersionId: string | null;
  acknowledgmentChecked: boolean;
};

export function canPublish(gate: PublishGate): boolean {
  return Boolean(
    gate.draftId &&
    gate.changeSummary.trim() &&
    DATE.test(gate.effectiveDate.trim()) &&
    gate.acknowledgmentVersionId &&
    gate.acknowledgmentChecked,
  );
}

/** The editor loaded a published version that is no longer the live one. */
export function isPublishStale(
  expectedCurrentVersionId: string | null,
  liveVersionId: string | null,
): boolean {
  if (!expectedCurrentVersionId) return false;
  return expectedCurrentVersionId !== liveVersionId;
}

/**
 * The acknowledgment checkbox is unticked every time the dialog opens and
 * every time the acknowledgment is reloaded. A second open starts unticked.
 */
export function acknowledgmentCheckedOnOpen(): false {
  return false;
}

export type TimelineItem = {
  id: string;
  at: string;
  label: string;
  detail?: string | null;
};

export function policyTimeline(input: {
  versions: Array<{
    id: string;
    versionNumber?: number | null;
    status?: string | null;
    publishedAt?: string | null;
    changeSummary?: string | null;
    publishAcknowledgment?: { version?: { versionNumber?: number | null } | null } | null;
  }>;
  events: Array<{
    id: string;
    action: string;
    createdAt: string;
    actorName?: string | null;
    versionId?: string | null;
  }>;
}): TimelineItem[] {
  const rows: Array<TimelineItem & { sort: number }> = [];
  for (const version of input.versions) {
    const ack = version.publishAcknowledgment?.version?.versionNumber;
    const at = version.publishedAt || '';
    rows.push({
      id: `version-${version.id}`,
      at,
      sort: at ? Date.parse(at) || 0 : 0,
      label: `Version ${version.versionNumber ?? '—'} · ${version.status ?? ''}`.trim(),
      detail: [version.changeSummary, ack != null ? `Acknowledgment v${ack} accepted` : null]
        .filter(Boolean)
        .join(' · '),
    });
  }
  for (const event of input.events) {
    rows.push({
      id: `event-${event.id}`,
      at: event.createdAt,
      sort: Date.parse(event.createdAt) || 0,
      label: event.action.replace(/_/g, ' '),
      detail: event.actorName ?? null,
    });
  }
  return rows.sort((a, b) => b.sort - a.sort).map(({ sort: _sort, ...item }) => item);
}
